import {
  EntityAboutSection,
  JobOpeningSkillsReadSection,
  useArtifactCanvas,
  Button,
  Card,
  CardContent,
  CardFooter,
  profileSectionEmptyCtaClass,
  profileSectionManageButtonClass,
} from '@yeshuman/sdk'
import { useCallback, useMemo, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { CircleAlert, FileText, Plus, Pencil } from 'lucide-react'

import { OpportunityDetailsCanvas } from '../opportunity-details-canvas'
import { JobDescriptionCanvas } from './job-description-canvas'
import { OpportunitySkillsCanvas } from '../opportunity-skills-canvas'
import {
  JobOpeningIdentityHeader,
  JobOpeningMetaLine,
  JobOpeningSalaryFooter,
  parseJobOpeningMetaFields,
} from '../job-opening-identity'
import type { OrganisationOpportunity } from '../../hooks/use-organisation-opportunities'
import { toOpportunityJobSummaryInput } from '../../utils/opportunity-job-meta'
import {
  formatPublishBlockerActionLabelsPhrase,
  listOpportunityPublishBlockers,
} from '../../utils/opportunity-publish-readiness'

interface JobWorkspaceSetupTabProps {
  opportunity: OrganisationOpportunity
  opportunityId: string
  orgName: string
}

export function JobWorkspaceSetupTab({
  opportunity,
  opportunityId,
  orgName,
}: JobWorkspaceSetupTabProps) {
  const queryClient = useQueryClient()
  const { openCanvas, closeCanvas } = useArtifactCanvas()
  const jobDetailsOpenKeyRef = useRef(0)
  const organisationSlug = opportunity.organisation_slug ?? ''
  const jobMeta = toOpportunityJobSummaryInput(opportunity)
  const parsedMeta = useMemo(() => parseJobOpeningMetaFields(jobMeta), [jobMeta])
  const { employmentType, locationText, workMode, salaryText } = parsedMeta
  const isDraft = opportunity.publication_status === 'draft'
  const publishBlockers = useMemo(
    () => (isDraft ? listOpportunityPublishBlockers(opportunity) : []),
    [isDraft, opportunity],
  )

  /** Any filled employment / place / salary — ignores “Updated …” alone. */
  const hasAnyJobDetails = Boolean(
    parsedMeta.employmentType || parsedMeta.locationWorkModeText || salaryText,
  )
  const detailsCompletelyEmpty = !hasAnyJobDetails
  const missingFieldsPhrase = useMemo(
    () => formatPublishBlockerActionLabelsPhrase(publishBlockers),
    [publishBlockers],
  )

  const patchCache = useCallback(
    (updated: OrganisationOpportunity) => {
      queryClient.setQueryData(['opportunity', opportunityId], updated)
      if (organisationSlug) {
        queryClient.setQueryData(['organisation-opportunity', organisationSlug, opportunityId], updated)
        void queryClient.invalidateQueries({ queryKey: ['organisation-opportunities', organisationSlug] })
      }
    },
    [queryClient, opportunityId, organisationSlug],
  )

  const openJobDetailsCanvas = useCallback(() => {
    jobDetailsOpenKeyRef.current += 1
    openCanvas({
      title: 'Edit job details',
      preventAutoFocus: true,
      content: (
        <OpportunityDetailsCanvas
          key={`job-details-${jobDetailsOpenKeyRef.current}`}
          organisationSlug={organisationSlug}
          opportunityId={opportunityId}
          opportunity={opportunity}
          onClose={closeCanvas}
          onSaved={patchCache}
        />
      ),
    })
  }, [opportunity, opportunityId, organisationSlug, openCanvas, closeCanvas, patchCache])

  const openJobDescriptionCanvas = useCallback(() => {
    openCanvas({
      title: 'Job description',
      preventAutoFocus: true,
      surface: {
        intent: 'edit',
        entity: 'opportunity',
        id: opportunityId,
        mode: 'canvas',
        fields: ['description'],
      },
      content: (
        <JobDescriptionCanvas
          opportunityId={opportunityId}
          opportunity={opportunity}
          onClose={closeCanvas}
          onSaved={patchCache}
        />
      ),
    })
  }, [opportunity, opportunityId, openCanvas, closeCanvas, patchCache])

  const openSkillsCanvas = useCallback(() => {
    openCanvas({
      title: 'Skills',
      preventAutoFocus: true,
      content: (
        <OpportunitySkillsCanvas
          organisationSlug={organisationSlug}
          opportunityId={opportunityId}
          opportunity={opportunity}
          onClose={closeCanvas}
          onSaved={patchCache}
        />
      ),
    })
  }, [opportunity, opportunityId, organisationSlug, openCanvas, closeCanvas, patchCache])

  const editJobDetailsAction = (
    <Button
      type="button"
      variant="ghostToolbar"
      size="iconSm"
      className={profileSectionManageButtonClass}
      aria-label="Edit job details"
      title="Edit job details"
      onClick={openJobDetailsCanvas}
    >
      <Pencil className="h-4 w-4" />
    </Button>
  )

  return (
    <div className="space-y-6">
      {opportunity.publication_status === 'archived' ? (
        <div className="rounded-md border border-muted bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
          This job is archived. Restore it to make changes.
        </div>
      ) : null}

      <Card className="group/section relative z-0">
        <CardContent className="space-y-3">
          <JobOpeningIdentityHeader
            title={opportunity.title}
            showPublicationBadge={false}
            organisationLabel={orgName}
            organisationLogoUrl={opportunity.organisation_logo_url}
            trailing={editJobDetailsAction}
          />

          {detailsCompletelyEmpty ? (
            <Button
              type="button"
              variant="outline"
              className={profileSectionEmptyCtaClass}
              onClick={openJobDetailsCanvas}
            >
              <Plus className="mr-2 h-4 w-4 shrink-0" aria-hidden />
              Add job details
            </Button>
          ) : (
            <>
              <JobOpeningMetaLine
                meta={jobMeta}
                employmentType={employmentType}
                locationText={locationText}
                workMode={workMode}
                publishedAt={opportunity.published_at}
                updatedAt={opportunity.updated_at}
                timestampVariant="interactive"
              />
              {publishBlockers.length > 0 && missingFieldsPhrase ? (
                <div
                  className="flex items-start gap-2 rounded-md border border-border/60 bg-muted/40 px-3 py-2.5"
                  role="status"
                >
                  <CircleAlert
                    className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground"
                    aria-hidden
                  />
                  <div className="min-w-0 text-sm leading-snug text-muted-foreground">
                    <span className="font-medium text-foreground">Not ready to publish yet</span>
                    <span className="text-xs">
                      {' — '}
                      still needs {missingFieldsPhrase}.{' '}
                    </span>
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      className="h-auto px-0 text-xs align-baseline"
                      onClick={openJobDetailsCanvas}
                    >
                      Edit job details
                    </Button>
                  </div>
                </div>
              ) : null}
            </>
          )}
        </CardContent>
        {salaryText ? (
          <CardFooter className="border-t border-border/60">
            <JobOpeningSalaryFooter meta={jobMeta} salaryLabel={salaryText} />
          </CardFooter>
        ) : null}
      </Card>

      <EntityAboutSection
        title="Job description"
        leadingIcon={FileText}
        subtitle="Full text for this role"
        body={opportunity.description}
        stripLeadingTitles={[opportunity.title]}
        emptyVariant="cta"
        emptyCtaLabel="Add description"
        onEdit={openJobDescriptionCanvas}
        editAriaLabel="Edit job description"
        editTitle="Edit job description"
      />

      <JobOpeningSkillsReadSection
        skills={opportunity.skills}
        onEdit={openSkillsCanvas}
        emptyMessage="Add skills"
        emptyVariant="cta"
      />
    </div>
  )
}
